import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, ArrowLeft } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import QuizIcon from "../../Images/QuizIcon.svg";
import AssignmentIcon from "../../Images/assignmentIcon.svg";
import LabIcon from "../../Images/Assignment.svg";
import DemoIcon from "../../Images/Quiz.svg";
import AssignmentSub from "../StudentMyPrograms/Assignment_sub";
import { renderStars } from "../../config/uiHelpers";
import { API_URL } from "../../config/api";

export default function StudentMLT() {
  const [expandedModule, setExpandedModule] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scheduleId = searchParams.get("schedule_id");

  const [schedule, setSchedule] = useState(null);
  const [progress, setProgress] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [labs, setLabs] = useState([]);
  const [demos, setDemos] = useState([]);
  const [assignmentSchedule, setAssignmentSchedule] = useState(null);
  const [labSchedule, setLabSchedule] = useState(null);
  const [demoSchedule, setDemoSchedule] = useState(null);
  const [quizSchedule, setQuizSchedule] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskType, setTaskType] = useState(null); // "assignment" | "lab" | "demo" | "quiz"
  const [certProgress, setCertProgress] = useState({
    percentage: 0,
    passingScore: 0,
    message: "Checking eligibility...",
    eligible: false,
  });

  // Fetch main data
  useEffect(() => {
    if (!scheduleId) {
      navigate("/myprograms");
      return;
    }

    const fetchData = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const headers = { Authorization: `Bearer ${token}` };

        const [scheduleRes, assignmentRes, quizRes, labRes, demoRes] =
          await Promise.all([
            axios.get(
              `${API_URL}/course-schedules/schedule_details/${scheduleId}`,
              { headers },
            ),
            axios.get(`${API_URL}/assignments/schedules/${scheduleId}`, {
              headers,
            }),
            axios.get(`${API_URL}/quiz/schedules/${scheduleId}`, { headers }),
            axios.get(`${API_URL}/labs/schedules/${scheduleId}`, { headers }),
            axios.get(`${API_URL}/demo/schedules/${scheduleId}`, { headers }),
          ]);

        const scheduleData = scheduleRes.data?.data ?? scheduleRes.data;
        setSchedule(scheduleData?.schedule ?? scheduleData);
        setProgress(scheduleData?.progress ?? null);
        setAssignmentSchedule(assignmentRes.data);
        setAssignments(assignmentRes.data?.assignments ?? []);
        setQuizSchedule(quizRes.data);
        setQuizzes(quizRes.data?.quizzes ?? []);
        setLabSchedule(labRes.data);
        setLabs(labRes.data?.labs ?? []);
        setDemoSchedule(demoRes.data);
        setDemos(demoRes.data?.demos ?? []);
      } catch (err) {
        console.error(
          "Error fetching data:",
          err.response?.data || err.message,
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [scheduleId, navigate]);

  // Fetch certificate progress
  useEffect(() => {
    if (!scheduleId) return;

    const fetchCertificateProgress = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const headers = {
          Authorization: `Bearer ${token}`,
        };

        const res = await axios.post(
          `${API_URL}/certificates/check-and-issue`,
          { schedule_id: scheduleId },
          { headers },
        );

        setCertProgress({
          percentage: Math.round(res.data.percentage || 0),
          passingScore: res.data.passing_score || 0,
          message: res.data.message || "Progress updated",
          eligible: res.data.eligible || false,
        });
      } catch (err) {
        console.error(
          "Certificate API Error:",
          err.response?.data || err.message,
        );
        setCertProgress((prev) => ({
          ...prev,
          message: "Unable to check certificate eligibility",
        }));
      }
    };

    fetchCertificateProgress();
  }, [scheduleId]);

  const toggleModule = (moduleId) => {
    setExpandedModule(expandedModule === moduleId ? null : moduleId);
  };

  const getCurrentModule = () => {
    // FIXED: Changed from course?.modules to schedule?.course?.modules
    if (!schedule?.course?.modules || !progress?.modules) return "Not Started";

    for (const module of schedule.course.modules) {
      const moduleProgress = progress.modules.find(
        (m) => m.module_id === module.module_id,
      );

      if (!moduleProgress || moduleProgress.status !== "COMPLETED") {
        return module.title;
      }
    }

    return "Completed";
  };

  const getLessonProgress = (moduleId, lessonId) => {
    if (!progress?.modules) return 0;

    const moduleProgress = progress.modules.find(
      (m) => m.module_id === moduleId,
    );
    if (!moduleProgress) return 0;

    if (moduleProgress.lessons?.completed?.includes(lessonId)) {
      return 100;
    }

    if (moduleProgress.lessons?.in_progress?.includes(lessonId)) {
      return 50;
    }

    return 0;
  };

  const getModuleData = (moduleId) => {
    if (!progress?.modules) {
      return { completed: 0, total: 0, percent: 0, status: "Not Started" };
    }

    const moduleProgress = progress.modules.find(
      (m) => m.module_id === moduleId,
    );
    if (!moduleProgress) {
      return { completed: 0, total: 0, percent: 0, status: "Not Started" };
    }

    const status =
      moduleProgress.status === "COMPLETED"
        ? "Completed"
        : moduleProgress.status === "IN_PROGRESS"
          ? "In Progress"
          : "Not Started";

    return {
      completed: moduleProgress.completed_lessons || 0,
      total: moduleProgress.total_lessons || 0,
      percent: moduleProgress.module_progress || 0,
      status: status,
    };
  };

  const openTaskModal = (type, data) => {
    setTaskType(type);
    setSelectedTask(data);
    setIsTaskModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading course details...</p>
        </div>
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Course not found</p>
          <button
            onClick={() => navigate("/myprograms")}
            className="px-4 py-2 bg-cyan-600 text-white rounded hover:bg-teal-500"
          >
            Back to My Programs
          </button>
        </div>
      </div>
    );
  }

  const course = schedule.course;
  const instructor = course.instructors?.[0]?.user?.full_name || "Instructor";
  const overallProgress = schedule?.overall_progress ?? 0;
  const isCompleted = new Date(schedule.end_date) < new Date();
  const isUpcoming = new Date(schedule.start_date) > new Date();

  return (
    <div className="min-h-screen bg-zinc-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/myprograms")}
              className="p-2 hover:bg-gray-200 rounded-full transition-colors"
            >
              <ArrowLeft size={24} className="text-gray-700" />
            </button>
            <h1 className="text-xl font-bold text-zinc-800">{course.title}</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Course Progress Card */}
            <div className="bg-white rounded-lg p-6 shadow-sm relative">
              <div className="flex items-start gap-8">
                {/* Progress Circle */}
                <div className="relative w-32 h-32 flex-shrink-0">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="#e5e7eb"
                      strokeWidth="12"
                      fill="none"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="#0891b2"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={2 * Math.PI * 56}
                      strokeDashoffset={
                        2 * Math.PI * 56 * (1 - overallProgress / 100)
                      }
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-3xl font-bold text-zinc-800">
                      {overallProgress}%
                    </span>
                  </div>
                </div>

                {/* Content Section */}
                <div className="flex-1 flex flex-col gap-7">
                  {/* Row 1 - Dates */}
                  <div className="flex items-start gap-16">
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-start text-gray-700 text-xs tracking-tight">
                        Course Start Date
                      </div>
                      <div className="flex items-start text-zinc-800 text-sm font-medium tracking-tight">
                        {new Date(schedule.start_date).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "2-digit",
                            year: "numeric",
                          },
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-start text-gray-700 text-xs tracking-tight">
                        Course Completion Date
                      </div>
                      <div className="flex items-start text-zinc-800 text-sm font-medium tracking-tight">
                        {new Date(schedule.end_date).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "2-digit",
                            year: "numeric",
                          },
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Row 2 - Passing Score, Instructor, Ratings */}
                  <div className="flex items-start gap-8">
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-start text-gray-700 text-xs tracking-tight">
                        Passing Condition Value
                      </div>
                      <div className="flex items-start text-zinc-800 text-sm font-medium tracking-tight">
                        {course.passing_score || "Null"}%
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-start text-gray-700 text-xs tracking-tight">
                        Instructor
                      </div>
                      <div className="flex items-start text-zinc-800 text-sm font-medium tracking-tight">
                        {instructor}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-start text-gray-700 text-xs tracking-tight">
                        Course Ratings
                      </div>
                      <div className="flex items-center gap-1.5">
                        {renderStars(schedule?.average_rating || 0)}
                        <span className="flex items-start text-neutral-700 text-[9px] leading-7">
                          (
                          {schedule?.average_rating
                            ? schedule.average_rating.toFixed(1)
                            : "0.0"}
                          )
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Row 3 - Current Module */}
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-start text-gray-700 text-xs tracking-tight">
                      Current Module
                    </div>
                    <div className="flex items-start text-zinc-800 text-sm font-medium tracking-tight">
                      {getCurrentModule()}
                    </div>
                  </div>
                </div>

                {/* Status Badge - Positioned Absolutely */}
                <div
                  className={`absolute top-[15px] right-6 px-4 py-[5px] rounded-[60px] flex items-center gap-1 ${
                    isCompleted ? "bg-sky-100" : "bg-emerald-100"
                  }`}
                >
                  <span
                    className={`text-sm ${
                      isCompleted ? "text-cyan-600" : "text-lime-600"
                    }`}
                  >
                    {isCompleted
                      ? "Completed"
                      : isUpcoming
                        ? "Upcoming"
                        : "Ongoing"}
                  </span>
                </div>
              </div>
            </div>

            {/* Progress Card */}
            <div className="bg-white rounded-lg p-8 shadow-sm w-full">
              <h2 className="text-base font-bold text-zinc-800 mb-4">
                {certProgress.eligible
                  ? "🎉 You did it! You passed the program"
                  : "⏳ Keep going! You haven't passed yet"}
              </h2>

              <p className="text-sm text-zinc-800 mb-6">
                You have achieved <b>{certProgress.percentage}%</b> of the total
                points in the program
              </p>

              <div className="relative w-full h-4 bg-zinc-300 rounded mb-6">
                <div
                  className={`absolute top-0 left-0 h-4 rounded transition-all duration-500 ${
                    certProgress.eligible ? "bg-green-800" : "bg-yellow-500"
                  }`}
                  style={{
                    width: `${certProgress.percentage}%`,
                  }}
                ></div>
              </div>

              <div className="flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 bg-emerald-700 rounded-full"></div>
                  <span className="text-sm text-neutral-700">
                    Earned Points
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 bg-zinc-300 rounded-full"></div>
                  <span className="text-sm text-neutral-700">
                    Available Points
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full"></div>
                  <span className="text-sm text-neutral-700">
                    Minimum required grade ({course.passing_score || "Null"}%)
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h2 className="flex items-start text-xl font-semibold text-zinc-800 mb-2">
                Module By Module Progress
              </h2>

              <div className="space-y-6">
                {course.modules && course.modules.length > 0 ? (
                  course.modules.map((module, index) => {
                    const { completed, total, percent, status } = getModuleData(
                      module.module_id,
                    );

                    return (
                      <div
                        key={module.module_id}
                        className="border border-gray-200 rounded-lg p-4 shadow-sm"
                      >
                        <div
                          className="flex items-center justify-between mb-3 cursor-pointer"
                          onClick={() =>
                            total > 0 && toggleModule(module.module_id)
                          }
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-base font-semibold text-zinc-800">
                              {status}
                            </span>
                          </div>
                          {total > 0 &&
                            (expandedModule === module.module_id ? (
                              <ChevronUp size={20} />
                            ) : (
                              <ChevronDown size={20} />
                            ))}
                        </div>

                        <div className="flex items-start mb-2">
                          <span className="text-base font-semibold text-zinc-800">
                            Module {index + 1}:{" "}
                          </span>
                          <span className="text-base text-neutral-700">
                            {module.title}
                          </span>
                        </div>

                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm text-zinc-800">
                            {percent}%
                          </span>
                          <span className="text-sm text-zinc-800">
                            {completed}/{total} Lessons
                          </span>
                        </div>

                        <div className="w-full h-2 bg-slate-100 rounded overflow-hidden">
                          <div
                            className="h-full bg-cyan-600 rounded transition-all duration-300"
                            style={{ width: `${percent}%` }}
                          ></div>
                        </div>

                        {expandedModule === module.module_id &&
                          module.lessons &&
                          module.lessons.length > 0 && (
                            <div className="mt-4 space-y-4">
                              {module.lessons.map((lesson, lessonIndex) => {
                                const assessmentTypes = lesson.assessment_list
                                  ? lesson.assessment_list
                                      .split(",")
                                      .map((a) => a.trim())
                                  : [];

                                return (
                                  <div
                                    key={lesson.lesson_id}
                                    className="bg-stone-50 border border-gray-200 rounded p-4 shadow-sm"
                                  >
                                    <div className="flex items-center justify-between mb-3">
                                      <div className="flex items-center gap-2 flex-1">
                                        <span className="text-[#4AACC5] font-medium text-sm whitespace-nowrap">
                                          Lesson {lessonIndex + 1}:
                                        </span>
                                        <span className="text-zinc-800 font-normal text-sm">
                                          {lesson.title}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="flex items-start justify-between mb-2">
                                      <div className="text-sm text-zinc-800">
                                        <span className="font-semibold">
                                          Type:{" "}
                                        </span>
                                        <span>
                                          {assessmentTypes.length > 0
                                            ? assessmentTypes
                                                .filter(
                                                  (type) =>
                                                    type.toLowerCase() !==
                                                    "upload lesson materials",
                                                )
                                                .join(" + ") ||
                                              lesson.lesson_type ||
                                              "Lecture"
                                            : lesson.lesson_type ||
                                              "Lecture + Practice"}
                                        </span>
                                      </div>

                                      {assessmentTypes.some((type) =>
                                        type
                                          .toLowerCase()
                                          .includes("upload lesson materials"),
                                      ) && (
                                        <a
                                          href="#"
                                          className="text-cyan-600 text-sm font-medium hover:underline"
                                          onClick={(e) => {
                                            e.preventDefault();
                                          }}
                                        >
                                          View Lesson Materials
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No modules available for this course
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Tasks and Reminders Card */}
            <div className="bg-white rounded-lg p-6 shadow-sm max-h-96 overflow-y-auto">
              <h3 className="flex items-start text-lg font-semibold text-zinc-800 mb-6">
                Tasks and Reminders
              </h3>

              {/* Pending Quizzes Section */}
              <div className="mb-6">
                <h4 className="flex items-start text-sm font-semibold text-zinc-800 mb-4">
                  Quizzes
                </h4>
                <div className="space-y-3">
                  {quizzes.length > 0 ? (
                    quizzes.map((quiz) => {
                      const minutes = quiz.time_limit_seconds
                        ? Math.floor(quiz.time_limit_seconds / 60)
                        : 0;
                      return (
                        <div
                          key={quiz.quiz_id}
                          className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="w-12 h-12 bg-cyan-50 rounded-lg flex items-center justify-center flex-shrink-0 border border-cyan-200">
                            <img
                              src={QuizIcon}
                              alt="Quiz"
                              className="w-7 h-7"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="text-sm font-medium text-zinc-800 mb-1">
                              {quiz.quiz_title}
                            </h5>
                            <div className="flex items-center gap-1 text-xs text-red-600 mb-1">
                              <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                              <span>
                                Submit Before:{" "}
                                {quizSchedule?.end_date
                                  ? new Date(
                                      quizSchedule.end_date,
                                    ).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "2-digit",
                                    })
                                  : "No deadline"}
                              </span>
                            </div>
                            <div className="flex items-center justify-center gap-4 text-xs text-gray-600 flex-nowrap whitespace-nowrap">
                              <span className="flex items-center gap-1">
                                <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                                {quiz.quiz_total_questions} Questions
                              </span>

                              <span className="flex items-center gap-1">
                                <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                                {minutes} Minutes
                              </span>
                            </div>
                          </div>
                          <button
                            className="px-4 py-1.5 bg-cyan-600 text-white text-xs font-medium rounded hover:bg-cyan-700 transition-colors flex-shrink-0"
                            onClick={() => openTaskModal("quiz", quiz)}
                          >
                            Start
                          </button>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-6 text-gray-500 text-sm">
                      No pending quizzes
                    </div>
                  )}
                </div>
              </div>

              {/* Assignment Section */}
              <div className="mb-6">
                <h4 className="flex items-start text-sm font-semibold text-zinc-800 mb-4">
                  Assignment
                </h4>
                <div className="space-y-3">
                  {assignments.length > 0 ? (
                    assignments.map((assignment) => (
                      <div
                        key={assignment.assignment_id}
                        className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="w-12 h-12 bg-cyan-50 rounded-lg flex items-center justify-center flex-shrink-0 border border-cyan-200">
                          <img
                            src={AssignmentIcon}
                            alt="Assignment"
                            className="w-7 h-7"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="text-sm font-medium text-zinc-800 mb-1">
                            {assignment.title}
                          </h5>
                          <div className="flex items-center gap-1 text-xs text-red-600">
                            <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                            <span>
                              Submit Before:{" "}
                              {assignmentSchedule?.end_date
                                ? new Date(
                                    assignmentSchedule.end_date,
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "2-digit",
                                  })
                                : "No deadline"}
                            </span>
                          </div>
                        </div>
                        <button
                          className="px-4 py-1.5 bg-cyan-600 text-white text-xs font-medium rounded hover:bg-cyan-700 transition-colors flex-shrink-0"
                          onClick={() =>
                            openTaskModal("assignment", assignment)
                          }
                        >
                          Start
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6 text-gray-500 text-sm">
                      No assignments available
                    </div>
                  )}
                </div>
              </div>

              {/* Lab Section */}
              <div className="mb-6">
                <h4 className="flex items-start text-sm font-semibold text-zinc-800 mb-4">
                  Lab
                </h4>
                <div className="space-y-3">
                  {labs.length > 0 ? (
                    labs.map((lab) => (
                      <div
                        key={lab.lab_id}
                        className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="w-12 h-12 bg-cyan-50 rounded-lg flex items-center justify-center flex-shrink-0 border border-cyan-200">
                          <img src={LabIcon} alt="Lab" className="w-7 h-7" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="text-sm font-medium text-zinc-800 mb-1">
                            {lab.title}
                          </h5>
                          <div className="flex items-center gap-1 text-xs text-red-600">
                            <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                            <span>
                              Submit Before:{" "}
                              {labSchedule?.end_date
                                ? new Date(
                                    labSchedule.end_date,
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "2-digit",
                                  })
                                : "No deadline"}
                            </span>
                          </div>
                        </div>
                        <button
                          className="px-4 py-1.5 bg-cyan-600 text-white text-xs font-medium rounded hover:bg-cyan-700 transition-colors flex-shrink-0"
                          onClick={() => openTaskModal("lab", lab)}
                        >
                          Start
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6 text-gray-500 text-sm">
                      No labs available
                    </div>
                  )}
                </div>
              </div>

              {/* Demo Section */}
              <div>
                <h4 className="flex items-start text-sm font-semibold text-zinc-800 mb-4">
                  Demo
                </h4>
                <div className="space-y-3">
                  {demos.length > 0 ? (
                    demos.map((demo) => (
                      <div
                        key={demo.demo_id}
                        className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="w-12 h-12 bg-cyan-50 rounded-lg flex items-center justify-center flex-shrink-0 border border-cyan-200">
                          <img src={DemoIcon} alt="Demo" className="w-7 h-7" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h5 className="text-sm font-medium text-zinc-800 mb-1">
                            {demo.title}
                          </h5>
                          <div className="flex items-center gap-1 text-xs text-red-600">
                            <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                            <span>
                              Submit Before:{" "}
                              {demoSchedule?.end_date
                                ? new Date(
                                    demoSchedule.end_date,
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "2-digit",
                                  })
                                : "No deadline"}
                            </span>
                          </div>
                        </div>
                        <button
                          className="px-4 py-1.5 bg-cyan-600 text-white text-xs font-medium rounded hover:bg-cyan-700 transition-colors flex-shrink-0"
                          onClick={() => openTaskModal("demo", demo)}
                        >
                          Start
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6 text-gray-500 text-sm">
                      No demos available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Task Modal */}
      <AssignmentSub
        isOpen={isTaskModalOpen}
        onClose={() => setIsTaskModalOpen(false)}
        task={selectedTask}
        type={taskType}
        scheduleId={scheduleId}
      />
    </div>
  );
}